/**
 * The threat model, assessed against live controls.
 *
 * The design decision worth naming: there is no score, no percentage and no overall "risk posture"
 * number anywhere on this screen. Nine threats, each `ok` / `warn` / `bad`, each with the names of
 * the controls that are failing or unmeasured. A single number would be the thing everybody quotes
 * and nobody can act on, and producing it would require deciding — silently, on the reader's
 * behalf — that an unmeasured control is worth some fraction of a working one.
 *
 * The "cannot be fully measured" band at the top exists so that a reader who notices seven threats
 * stuck permanently at amber learns why in the first five seconds rather than concluding the
 * product is broken.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import type { StatusTone } from "@hub/core/browser";
import { useApi } from "../lib/api";
import { Card, Pill } from "../components";

interface ControlRef { id: string; name: string; detail?: string; why?: string; assurance?: string }

interface Assessment {
  threat: {
    id: string; name: string; mechanism: string; whyItIsHard: string;
    stride: string[]; layers: string[]; appliesTo: string[];
  };
  residual: StatusTone;
  summary: string;
  inForce: ControlRef[];
  failing: ControlRef[];
  unknown: ControlRef[];
}

export function Threats() {
  const { data, error, loading } = useApi<{ assessments: Assessment[]; neverFullyMeasurable: string[] }>("/v1/threats");
  const [open, setOpen] = useState<string | null>(null);

  if (loading) return <p className="empty">Running the control probes…</p>;
  if (error) return <p className="err">{error.message}{error.hint ? ` — ${error.hint}` : ""}</p>;
  if (!data) return null;

  const bad = data.assessments.filter((a) => a.residual === "bad");
  const warn = data.assessments.filter((a) => a.residual === "warn");

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Threat model</h1>
          <p className="sub">
            Nine risk classes — OWASP&apos;s seven agentic classes and two this catalogue adds — each
            assessed against the controls that answer it. A
            control counts as mitigating only when a probe says it is <strong>in force</strong> —
            not when somebody has ticked it.
          </p>
        </div>
        <Link className="btn" to="/assurance">Controls &amp; frameworks</Link>
      </div>

      <div className="grid k3" style={{ marginBottom: 14 }}>
        <div className="card kpi">
          <div className="label">Threats with a failing control</div>
          <div className="value" style={{ color: bad.length > 0 ? "var(--bad)" : "var(--ok)" }}>{bad.length}</div>
          <div className="delta">Something measurable is broken. This is the list to work from.</div>
        </div>
        <div className="card kpi">
          <div className="label">Threats with unmeasured controls</div>
          <div className="value" style={{ color: "var(--warn)" }}>{warn.length}</div>
          <div className="delta">Nothing is failing, and nothing proves it is working either.</div>
        </div>
        <div className="card kpi">
          <div className="label">Fully measured and in force</div>
          <div className="value" style={{ color: "var(--ok)" }}>
            {data.assessments.filter((a) => a.residual === "ok").length}
          </div>
          <div className="delta">Every control answered by a live query.</div>
        </div>
      </div>

      {data.neverFullyMeasurable.length > 0 && (
        <div className="not-live" role="status" style={{ marginBottom: 14 }}>
          <strong>{data.neverFullyMeasurable.length} of these can never show green from here.</strong>{" "}
          Their central controls live in your agent runtime — how prompts are assembled, whether a
          sandbox boundary holds — and nothing in this database can see them. They are amber for as
          long as that is true, which is honest rather than broken. Marking them green would be the
          most dangerous false reassurance on this page.
        </div>
      )}

      <div className="stack">
        {data.assessments.map((a) => (
          <Card key={a.threat.id} testId="threat">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <strong style={{ fontSize: 15 }}>{a.threat.name}</strong>
                  <Pill tone={a.residual}>
                    {a.residual === "ok" ? "controls in force"
                      : a.residual === "warn" ? "unmeasured" : "control failing"}
                  </Pill>
                  {a.threat.appliesTo.includes("human") && <Pill tone="dim">people too</Pill>}
                </div>
                <p className="sub" style={{ marginTop: 6, fontSize: 13 }}>{a.threat.mechanism}</p>
                {/* --muted, not --dim. `--dim` is the token for non-essential text and this is
                    the sentence that explains why the threat is modelled at all; the design system
                    documents --muted on --surface at 6.1:1 and requires 4.5:1 for body text. */}
                <p className="sub" style={{ marginTop: 4, fontSize: 12.5 }}>
                  <strong style={{ color: "var(--text)" }}>Why it is hard: </strong>{a.threat.whyItIsHard}
                </p>
              </div>
              <button className="btn small" onClick={() => setOpen(open === a.threat.id ? null : a.threat.id)}>
                {open === a.threat.id ? "Hide controls" : "Controls"}
              </button>
            </div>

            <p style={{
              fontSize: 12.5, marginTop: 10, marginBottom: 0,
              color: a.residual === "bad" ? "var(--bad)" : a.residual === "warn" ? "var(--warn)" : "var(--muted)",
            }}>
              {a.summary}
            </p>

            {open === a.threat.id && (
              <div style={{ marginTop: 14, borderTop: "1px solid var(--line-soft)", paddingTop: 12 }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12, fontSize: 12 }}>
                  <span style={{ color: "var(--muted)" }}>STRIDE: {a.threat.stride.join(", ")}</span>
                  <span style={{ color: "var(--dim)" }}>·</span>
                  <span style={{ color: "var(--muted)" }}>Layer: {a.threat.layers.join(", ")}</span>
                </div>
                <ControlGroup title="Failing" tone="bad" items={a.failing} showDetail />
                <ControlGroup title="Cannot be measured from here" tone="warn" items={a.unknown} showWhy />
                <ControlGroup title="In force" tone="ok" items={a.inForce} />
              </div>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}

function ControlGroup({ title, tone, items, showDetail, showWhy }: {
  title: string; tone: StatusTone; items: ControlRef[]; showDetail?: boolean; showWhy?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: `var(--${tone === "ok" ? "ok" : tone})`, fontWeight: 600, marginBottom: 6 }}>
        {title} ({items.length})
      </div>
      <div className="stack" style={{ gap: 6 }}>
        {items.map((c) => (
          <div key={c.id} style={{ fontSize: 12.5, paddingLeft: 10, borderLeft: `2px solid var(--${tone === "ok" ? "ok" : tone})` }}>
            <div>{c.name}</div>
            {showDetail && c.detail && <div style={{ color: "var(--muted)" }}>{c.detail}</div>}
            {showWhy && c.why && (
              <div style={{ color: "var(--muted)" }}>
                {c.why}
                {c.assurance === "manual" && (
                  <> <Pill tone="dim">manual for ever</Pill></>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
